/** Moving a cursor around the entry grid.
 *
 *  Its own file, with no imports, for the reason `NewTasks.rows.ts` gives: the grid
 *  reaches the auth context and the router, so a test that imported the page to reach
 *  one pure function would need a browser. The arithmetic is worth testing; the page
 *  is not what is being tested.
 *
 *  A cell is `{r, c}` - a row index and a column index into the grid's nine columns.
 *  Nothing here knows what is IN a column; that belongs to `NewTasks.clip.ts`.
 */
export type Cell = { r: number; c: number }
export type Bounds = { rows: number; cols: number }

/** A rectangle of cells, normalised so `r0 <= r1` and `c0 <= c1`. */
export type Rect = { r0: number; c0: number; r1: number; c1: number }

const clamp = (n: number, max: number) => Math.max(0, Math.min(n, max - 1))

/** Where an arrow key lands, clamped to the grid.
 *
 *  Clamped rather than wrapped. Wrapping from the last column to the first row is a
 *  jump nobody asked for, and in a grid whose rows are being typed into it would move
 *  the caret somewhere off screen. A spreadsheet stops at the edge; so does this.
 *
 *  Returns the SAME cell at an edge rather than null, so a caller can move
 *  unconditionally and let the result be a no-op.
 */
export function moveCursor(cur: Cell, key: string, b: Bounds): Cell {
  const at = (r: number, c: number) => ({ r: clamp(r, b.rows), c: clamp(c, b.cols) })
  switch (key) {
    case 'ArrowUp': return at(cur.r - 1, cur.c)
    case 'ArrowDown': return at(cur.r + 1, cur.c)
    case 'ArrowLeft': return at(cur.r, cur.c - 1)
    case 'ArrowRight': return at(cur.r, cur.c + 1)
    // Home and End are along the row; with the platform modifier they are the whole
    // grid, which is the pair every spreadsheet uses.
    case 'Home': return at(cur.r, 0)
    case 'End': return at(cur.r, b.cols)
    case 'CtrlHome': return at(0, 0)
    case 'CtrlEnd': return at(b.rows, b.cols)
    default: return cur
  }
}

/** The rectangle two cells describe, whichever corner came first.
 *
 *  Dragging up-and-left is as ordinary as down-and-right, and an un-normalised rect
 *  silently selects nothing: `r0 > r1` makes every `inRect` test false, so the
 *  highlight vanishes and a copy comes out empty.
 */
export function normalizeRect(anchor: Cell, cur: Cell): Rect {
  return {
    r0: Math.min(anchor.r, cur.r), r1: Math.max(anchor.r, cur.r),
    c0: Math.min(anchor.c, cur.c), c1: Math.max(anchor.c, cur.c),
  }
}

export function inRect(rect: Rect, r: number, c: number): boolean {
  return r >= rect.r0 && r <= rect.r1 && c >= rect.c0 && c <= rect.c1
}

/** How many cells a rectangle covers. Drives the "is this more than one cell"
 *  question that decides whether a copy is the grid's or the focused input's. */
export function rectSize(rect: Rect): number {
  return (rect.r1 - rect.r0 + 1) * (rect.c1 - rect.c0 + 1)
}

/** Where Tab goes from here, and whether it has to make a row to get there.
 *
 *  Tab already walks the grid cell by cell without any help: every `<td>` holds
 *  exactly one control, so document order IS cell order, and Tab off one row's last
 *  cell already lands on the next row's Task box. The one place it runs out is the
 *  very last cell of the very last row, where focus escapes to the buttons under the
 *  grid - so a list can only grow by reaching for "Add row".
 *
 *  Two callers need to agree about this: the Tab branch in the grid's key handler,
 *  and the exit from an open picker's panel. Hence one function rather than the same
 *  arithmetic twice.
 *
 *  `addRow` is a REQUEST, not a promise. The cap belongs to the caller, which is the
 *  only side that knows how many rows there already are.
 *
 *  Column 0 is the row number and holds no control, so the cell after the last column
 *  is the next row's column 1 - never column 0.
 */
export function tabNext(cur: Cell, b: Bounds): { cell: Cell; addRow: boolean } {
  if (cur.c < b.cols - 1) return { cell: { r: cur.r, c: cur.c + 1 }, addRow: false }
  const last = cur.r >= b.rows - 1
  return { cell: { r: cur.r + 1, c: 1 }, addRow: last }
}

/** How many columns a row really has.
 *
 *  A row is two visual lines: the five fields always filled, then a quieter strip of
 *  four that most rows never touch. The strip is rendered only once the row has a
 *  task — a row with no task is not going to be filed, so offering it a priority and
 *  a run-until date is noise.
 *
 *  Which means the grid is NOT a rectangle, and the cursor has to know: on a blank
 *  row columns 6-9 do not exist in the DOM at all, so moving into one would land the
 *  caret nowhere and the keyboard would appear to die. Passing this as `bounds.cols`
 *  makes `moveCursor`'s existing clamp do the right thing for free, and makes Tab off
 *  the Date cell of a blank row go to the next row's Task - which is where somebody
 *  would expect it to go anyway.
 */
export const FIRST_LINE_COLS = 6

export const colsIn = (hasTask: boolean, all: number) =>
  hasTask ? all : FIRST_LINE_COLS
